import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { catchError, from, map, Observable, of, switchMap } from 'rxjs';
import { EnrollmentApplicationStatus, ProgramTypeKey } from '@k12/models';
import { EntraService } from '@k12/entra';
import { holdsOnly, resolveUserAccess } from '../auth/user-access';
import { EnrollmentAccessService } from '../enrollment';

/** Where the wizard lives in every registration portal. */
const ENROLLMENT_PATH = '/enrollment';

/** Where a closed enrollment period sends the user. */
const CLOSED_PATH = '/closed';

/** The portal's default route, which MsalGuard protects. */
const ROOT_PATH = '/';

/**
 * Gate for the /enrollment route. `programType` is fixed per portal, because each registration
 * portal serves exactly one registration program.
 *
 * A 401 is the one failure that must not fall through: the wizard cannot bootstrap without an
 * authenticated probe, so admitting the user renders a blank page. Every other failure falls through
 * to the wizard, which still has the server's own period check behind it on create and submit.
 */
export function enrollmentGuard(programType: ProgramTypeKey): CanActivateFn {
  return (route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> => {
    const access = inject(EnrollmentAccessService);
    const router = inject(Router);

    // An explicit ?sessionId= is an in-app hand-off that already resolved which application to open
    // (EnrollmentApplyService does this). Re-probing would be redundant and could fight it.
    if (route.queryParamMap.get('sessionId')) {
      return of(true);
    }

    if (route.queryParamMap.get('taskId') && route.queryParamMap.get('subTaskId')) {
      return of(true);
    }

    return access.getStatus(programType).pipe(
      map((status) => resolveWizardEntry(status, programType, router, route)),
      catchError((error: unknown) => {
        // A 401 means the request went out without a token, so the wizard has nothing to bootstrap
        // from either. Send them to the root and let MsalGuard, which owns authentication, sort it
        // out rather than rendering an empty page.
        if (error instanceof HttpErrorResponse && error.status === 401) {
          console.error('[enrollmentGuard] Status probe was unauthenticated; deferring to MsalGuard.', error);
          return of(router.createUrlTree([ROOT_PATH]));
        }
        console.error('[enrollmentGuard] Status probe failed; allowing the wizard through.', error);
        return of(true);
      })
    );
  };
}

/**
 * Gate for a portal's default route. A user whose only roles are the portal's ".New" roles (or who
 * has no roles at all yet) has nothing to see on a dashboard, so they are pushed into the wizard,
 * their in-progress application, or the closed-period page as their status dictates.
 *
 * @param programType the registration program this portal serves
 * @param newUserRoles every spelling of this portal's "new registrant" role
 */
export function newUserEnrollmentRedirect(
  programType: ProgramTypeKey,
  newUserRoles: string[]
): CanActivateFn {
  return (): Observable<boolean | UrlTree> => {
    const msalService = inject(MsalService);
    const entraService = inject(EntraService);
    const access = inject(EnrollmentAccessService);
    const router = inject(Router);

    return from(resolveUserAccess(msalService, entraService)).pipe(
      switchMap((userAccess) => {
        // Null means MsalGuard is still driving the login, or the probe failed. Deciding on either
        // would abort the login or bounce a fully provisioned user off their own dashboard, so the
        // only safe answer is to leave them where they are.
        if (!userAccess) {
          return of(true);
        }

        // Vacuously true when they hold no app roles at all, which is the pre-provisioning case.
        // Resolved through Graph rather than read straight off the token, because an absent roles
        // claim is not evidence of an absent role.
        if (!holdsOnly(userAccess.appRoles, newUserRoles)) {
          return of(true);
        }

        return access
          .getStatus(programType)
          .pipe(map((status) => resolveDashboardEntry(status, programType, router)));
      }),
      catchError((error) => {
        console.error('[newUserEnrollmentRedirect] Status probe failed; leaving the user in place.', error);
        return of(true);
      })
    );
  };
}

/**
 * Decision mapping for a user already heading INTO the wizard.
 *
 * Resume returns a UrlTree rather than true so the URL carries the session: a refresh then resumes
 * directly instead of re-probing, and it matches the shape EnrollmentApplyService already navigates
 * to. The guard re-runs on that navigation and short-circuits on the sessionId, so this cannot loop.
 */
function resolveWizardEntry(
  status: EnrollmentApplicationStatus,
  programType: ProgramTypeKey,
  router: Router,
  route: ActivatedRouteSnapshot
): boolean | UrlTree {
  switch (status.decision) {
    case 'AlreadySubmitted':
      return router.createUrlTree([ROOT_PATH]);
    case 'Resume':
      return router.createUrlTree([ENROLLMENT_PATH], {
        queryParams: {
          sessionId: status.sessionId,
          programType,
          programToken: status.enrollmentProgramToken
        }
      });
    case 'Start':
      // A bare /enrollment?programType= has no token for the renderer to bootstrap from. Supplying
      // the resolved one re-enters this guard, which then sees the token and lets it through.
      if (!route.queryParamMap.get('programToken') && status.enrollmentProgramToken) {
        return router.createUrlTree([ENROLLMENT_PATH], {
          queryParams: { programType, programToken: status.enrollmentProgramToken }
        });
      }
      return true;
    case 'Closed':
    default:
      return router.createUrlTree([CLOSED_PATH], { queryParams: { programType } });
  }
}

/** Decision mapping for a new user landing on a portal's default route. */
function resolveDashboardEntry(
  status: EnrollmentApplicationStatus,
  programType: ProgramTypeKey,
  router: Router
): boolean | UrlTree {
  switch (status.decision) {
    // Submitted registrants belong on the dashboard, which is where they already are. Redirecting them
    // to /enrollment-complete instead looks tempting -- a caller still holding only ".New" has not had
    // the access grant that ends provisioning -- but it loops: that page forwards to the dashboard as
    // soon as the domain record is readable, and the role claim is not reissued mid-session, so the two
    // routes bounce forever whenever the record exists but the grant has not landed.
    case 'AlreadySubmitted':
      return true;
    case 'Resume':
      return router.createUrlTree([ENROLLMENT_PATH], {
        queryParams: {
          sessionId: status.sessionId,
          programType,
          programToken: status.enrollmentProgramToken
        }
      });
    case 'Start':
      return status.enrollmentProgramToken
        ? router.createUrlTree([ENROLLMENT_PATH], {
            queryParams: { programType, programToken: status.enrollmentProgramToken }
          })
        : true;
    case 'Closed':
    default:
      return router.createUrlTree([CLOSED_PATH], { queryParams: { programType } });
  }
}
